/**
 * Sentry Context Management (Web Platform)
 *
 * Web-specific implementation using @sentry/browser SDK.
 * This file is automatically selected by Metro bundler for web builds.
 *
 * Refactored: 2026-01-09 (Fix Circular Import Bug)
 * - Extracted from shared context.ts to fix web/native SDK coupling
 * - Uses @sentry/browser instead of @sentry/react-native
 * - Imports from './config' instead of './analytics' to break circular dependency
 */

import * as Sentry from "@sentry/browser";
import type { SeverityLevel } from "@sentry/browser";
import { Platform } from "react-native";
import { scrubObject } from "./scrubbers";

// ============================================================================
// Sentry Context Management (Web-Specific Implementation)
// ============================================================================

/**
 * Phase 3 Observability: Set requestId for E2E trace correlation
 * Enables filtering in Sentry UI: request_id:abc-123-def
 * Also correlates with database logs via get_analysis_trace_by_id()
 *
 * @param requestId - UUID from Edge Function error response
 */
export function setRequestIdContext(requestId: string) {
  if (Sentry) {
    Sentry.setTag?.('request_id', requestId);
    Sentry.setContext?.('request', {
      request_id: requestId,
      timestamp: new Date().toISOString(),
    });
  }
}

/**
 * Phase 2: Set analysis metadata context for debugging
 * Only visible when errors occur - NOT sent as separate events
 * M4.1: Defense-in-depth scrubbing at call site (also scrubbed in beforeSend)
 */
export function setAnalysisContext(metadata: any) {
  if (Sentry) {
    Sentry.setContext('analysis_metadata', scrubObject(metadata));
  }
}

/**
 * Phase 2: Set device context for debugging
 * Only visible when errors occur - NOT sent as separate events
 * M4.1: Defense-in-depth scrubbing at call site
 */
export function setDeviceContext(device: any) {
  if (Sentry) {
    Sentry.setContext('device_metadata', scrubObject(device));
  }
}

/**
 * Phase 2: Set session context for debugging
 * Only visible when errors occur - NOT sent as separate events
 * M4.1: Defense-in-depth scrubbing at call site
 */
export function setSessionContext(session: any) {
  if (Sentry) {
    Sentry.setContext('session_metadata', scrubObject(session));
  }
}

/**
 * Phase 2: Clear analysis context after successful completion
 */
export function clearAnalysisContext() {
  if (Sentry) {
    Sentry.setContext('analysis_metadata', null);
  }
}

/**
 * Phase 3: Add technical breadcrumb for error reconstruction
 * Breadcrumbs are ONLY attached to error events, not sent standalone
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, any>,
  level?: string
) {
  if (Sentry) {
    Sentry.addBreadcrumb({
      category,
      message,
      data,
      level: (level || 'info') as SeverityLevel,
      timestamp: Date.now() / 1000,
    });
  }
}

/**
 * Phase 4: Start performance span (SDK v8+ API)
 * Returns inactive span object for manual span management
 *
 * Migration Notes (2026-01-09):
 * - Migrated from deprecated startTransaction() (removed in SDK v8)
 * - Using startInactiveSpan() for manual control (vs startSpan callback)
 * - Compatible with @sentry/browser v10+
 *
 * @see https://docs.sentry.io/platforms/javascript/migration/v7-to-v8/v8-new-performance-api/
 */
export function startTransaction(name: string, op: string) {
  if (Sentry && Sentry.startInactiveSpan) {
    return Sentry.startInactiveSpan({
      name,
      op,
    });
  }
  return null;
}

// ============================================================================
// Tier Tags (Phase 2: Debugging Quality)
// ============================================================================

/**
 * Set tier-related tags for filtering in Sentry dashboard
 * Enables filtering errors by: tier, platform, quota_source
 *
 * @param tier - User's subscription tier (anonymous, free, drama_plus, dramax)
 * @param quotaSource - Source of quota (anonymous, registered, family_member, family_admin)
 * @param familyPlanId - Family plan ID (if applicable, truncated for privacy)
 */
export function setTierTags(
  tier?: string,
  quotaSource?: string,
  familyPlanId?: string
) {
  if (!Sentry) return;

  // Platform tag (enables platform-specific filtering)
  const platformTag = Platform.OS === 'web' ? 'web' : Platform.OS;
  Sentry.setTag?.('platform', platformTag);

  if (tier) {
    Sentry.setTag?.('user_tier', tier);
  }

  if (quotaSource) {
    Sentry.setTag?.('quota_source', quotaSource);
  }

  if (familyPlanId) {
    // Truncate for privacy
    Sentry.setTag?.('family_plan', familyPlanId.substring(0, 8));
  }
}

// ============================================================================
// Business Impact Tags (Phase 3: Revenue-Critical Debugging)
// ============================================================================

/**
 * Business impact context for revenue-critical error prioritization
 * Enables filtering: is_paying, subscription.status, subscription.provider
 *
 * @param isPaying - Whether user is on a paid plan
 * @param subscriptionStatus - active, trialing, canceled, past_due
 * @param subscriptionProvider - polar, revenuecat, stripe, none
 */
export function setBusinessImpactTags(
  isPaying?: boolean,
  subscriptionStatus?: 'active' | 'trialing' | 'canceled' | 'past_due' | 'none',
  subscriptionProvider?: 'polar' | 'revenuecat' | 'stripe' | 'none'
) {
  if (!Sentry) return;

  if (isPaying !== undefined) {
    Sentry.setTag?.('is_paying', isPaying ? 'true' : 'false');
  }

  if (subscriptionStatus) {
    Sentry.setTag?.('subscription.status', subscriptionStatus);
  }

  if (subscriptionProvider) {
    Sentry.setTag?.('subscription.provider', subscriptionProvider);
  }
}

// ============================================================================
// AI Agent Context (Phase 3: LLM Error Isolation)
// ============================================================================

/**
 * AI agent execution context for LLM error debugging
 * Enables filtering by: agent_type, provider, model_family
 *
 * Provider-aware fingerprinting: group LLM errors by ai.provider to isolate vendor outages
 *
 * @param agentType - Agent type from agent_executions table (17 types)
 * @param provider - LLM provider (groq, openai, anthropic, google)
 * @param modelFamily - Model family (llama, gpt, claude, gemini)
 */
export function setAIContext(
  agentType?: string,
  provider?: 'groq' | 'openai' | 'anthropic' | 'google' | string,
  modelFamily?: string
) {
  if (!Sentry) return;

  if (agentType) {
    Sentry.setTag?.('ai.agent_type', agentType);
  }

  if (provider) {
    Sentry.setTag?.('ai.provider', provider);
  }

  if (modelFamily) {
    Sentry.setTag?.('ai.model_family', modelFamily);
  }

  // Set AI context for rich debugging
  Sentry.setContext?.('ai_execution', {
    agent_type: agentType,
    provider,
    model_family: modelFamily,
    timestamp: new Date().toISOString(),
  });
}

// ============================================================================
// Quota Context (Phase 3: Rate Limit Debugging)
// ============================================================================

/**
 * Quota usage context for rate limit error debugging
 * Uses bucketing to avoid high-cardinality tags
 *
 * @param used - Number of analyses used
 * @param limit - Total quota limit
 * @param blocked - Whether user is currently blocked
 * @param resetAt - ISO timestamp of quota reset
 */
export function setQuotaContext(
  used?: number,
  limit?: number,
  blocked?: boolean,
  resetAt?: string
) {
  if (!Sentry) return;

  // Tags: low-cardinality for filtering
  if (blocked !== undefined) {
    Sentry.setTag?.('quota.blocked', blocked ? 'true' : 'false');
  }

  // Bucket usage percentage
  if (used !== undefined && limit !== undefined && limit > 0) {
    const usagePercent = (used / limit) * 100;
    let usageBucket: string;
    if (usagePercent < 25) usageBucket = 'low';
    else if (usagePercent < 50) usageBucket = 'medium';
    else if (usagePercent < 75) usageBucket = 'high';
    else if (usagePercent < 100) usageBucket = 'critical';
    else usageBucket = 'exhausted';

    Sentry.setTag?.('quota.usage_bucket', usageBucket);
  }

  // Context: rich data for error detail view
  Sentry.setContext?.('quota', {
    used,
    limit,
    remaining: limit !== undefined && used !== undefined ? Math.max(0, limit - used) : undefined,
    blocked,
    reset_at: resetAt,
  });
}

// ============================================================================
// Analysis Context (Phase 3: Analysis Error Debugging)
// ============================================================================

/**
 * Analysis-specific context for drama analysis error debugging
 * Uses bucketing for complexity
 *
 * @param inputLength - Character count of input text
 * @param cached - Whether result was from cache
 * @param complexityScore - 0-100 complexity score (will be bucketed)
 * @param analysisId - Analysis ID (truncated for privacy)
 */
export function setAnalysisErrorContext(
  inputLength?: number,
  cached?: boolean,
  complexityScore?: number,
  analysisId?: string
) {
  if (!Sentry) return;

  // Tags: low-cardinality for filtering
  if (cached !== undefined) {
    Sentry.setTag?.('analysis.cached', cached ? 'true' : 'false');
  }

  // Bucket input length
  if (inputLength !== undefined) {
    let lengthBucket: string;
    if (inputLength < 500) lengthBucket = 'short';
    else if (inputLength < 1000) lengthBucket = 'medium';
    else if (inputLength < 2000) lengthBucket = 'long';
    else lengthBucket = 'very_long';

    Sentry.setTag?.('analysis.length_bucket', lengthBucket);
  }

  // Bucket complexity score
  if (complexityScore !== undefined) {
    let complexityBucket: string;
    if (complexityScore < 30) complexityBucket = 'simple';
    else if (complexityScore < 60) complexityBucket = 'moderate';
    else complexityBucket = 'complex';

    Sentry.setTag?.('analysis.complexity_bucket', complexityBucket);
  }

  // Context: rich data
  Sentry.setContext?.('analysis_error', {
    input_length: inputLength,
    cached,
    complexity_score: complexityScore,
    analysis_id: analysisId ? analysisId.substring(0, 8) : undefined,
  });
}

/**
 * Set platform tag on Sentry init
 * Call from initSentry() to set default platform tag
 */
export function setPlatformTag() {
  if (!Sentry) return;

  const platformTag = Platform.OS === 'web' ? 'web' : Platform.OS;
  Sentry.setTag?.('platform', platformTag);
  Sentry.setTag?.('runtime', 'client');
}

// Export Sentry for direct access in analytics.web.ts
export { Sentry };
